import {
  Component,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  NgZone,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as L from 'leaflet';
import { Posto } from '@core/models/models';
import { ProvinciaMapData } from '@shared/data/angola-map.data';

declare global {
  interface Window {
    _postoVer: (id: number) => void;
    _postoEdit: (id: number) => void;
  }
}

@Component({
  selector: 'app-mapa-geral',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mapa-geral.component.html',
  styleUrls: ['./mapa-geral.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapaGeralComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() postos: Posto[] = [];
  @Input() height = '580px';
  @Input() showToolbar = true;
  @Input() showCriarBtn = true;

  @Output() criarPosto = new EventEmitter<{ provincia?: string }>();
  @Output() verPosto = new EventEmitter<Posto>();
  @Output() editarPosto = new EventEmitter<Posto>();
  @Output() eliminarPosto = new EventEmitter<Posto>();
  @Output() provinciaSelect = new EventEmitter<ProvinciaMapData>();

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly ngZone = inject(NgZone);

  vista: 'mapa' | 'tabela' = 'tabela';

  private map: L.Map | null = null;
  private markersLayer: L.LayerGroup = L.layerGroup();

  ngAfterViewInit(): void {
    this.setupPopupCallbacks();
    if (this.vista === 'mapa') {
      setTimeout(() => this.initMap(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['postos'] && this.map) {
      this.ngZone.runOutsideAngular(() => this.renderMarkers());
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    delete (window as any)._postoVer;
    delete (window as any)._postoEdit;
  }

  private initMap(): void {
    const container = document.getElementById('postos-leaflet-map');
    if (!container || this.map) return;

    this.ngZone.runOutsideAngular(() => {
      this.map = L.map('postos-leaflet-map', {
        center: [-11.5, 17.5],
        zoom: 6,
        zoomControl: false,
        minZoom: 5,
        maxZoom: 18,
      });

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
        {
          attribution: '© OpenStreetMap © CartoDB',
          subdomains: 'abcd',
          maxZoom: 19,
        },
      ).addTo(this.map);

      L.control.zoom({ position: 'bottomright' }).addTo(this.map);

      this.markersLayer = L.layerGroup().addTo(this.map);
      this.renderMarkers();
    });
  }

  private renderMarkers(): void {
    if (!this.map) return;

    this.markersLayer.clearLayers();

    const postosComCoords = this.postos.filter(
      (p) =>
        p.latitude != null &&
        p.longitude != null &&
        p.latitude !== 0 &&
        p.longitude !== 0 &&
        !isNaN(Number(p.latitude)) &&
        !isNaN(Number(p.longitude)),
    );

    postosComCoords.forEach((p) => {
      const risco = (p.risco || 'baixo').toLowerCase();

      const icon = L.divIcon({
        className: '',
        html: `<div class="posto-marker risco-${risco} ${p.ativo ? 'ativo' : 'inativo'}">
                 <i class="ri-map-pin-fill pm-pin"></i>
                 <div class="pm-pulse"></div>
               </div>`,
        iconSize: [32, 40],
        iconAnchor: [16, 40],
        popupAnchor: [0, -42],
      });

      const marker = L.marker([Number(p.latitude), Number(p.longitude)], {
        icon,
      });
      marker.bindPopup(this.buildPopupHtml(p), {
        maxWidth: 300,
        className: 'posto-popup',
      });
      this.markersLayer.addLayer(marker);
    });

    const mapSize = this.map.getSize();
    const isMapVisible =
      this.vista === 'mapa' && mapSize.x > 0 && mapSize.y > 0;

    if (postosComCoords.length > 0 && isMapVisible) {
      const bounds = L.latLngBounds(
        postosComCoords.map(
          (p) => [Number(p.latitude), Number(p.longitude)] as L.LatLngTuple,
        ),
      );
      this.map.flyToBounds(bounds, {
        padding: [60, 60],
        maxZoom: 14,
        duration: 0.8,
      });
    }

    this.cdr.markForCheck();
  }

  private buildPopupHtml(p: Posto): string {
    const risco = (p.risco || 'baixo').toLowerCase();
    const riscoColor = this.getRiscoColor(p.risco);
    const riscoIcone =
      risco === 'baixo'
        ? 'ri-shield-check-line'
        : risco === 'moderado'
          ? 'ri-shield-line'
          : risco === 'medio'
            ? 'ri-shield-line'
            : 'ri-shield-cross-line';

    const estadoHtml = p.ativo
      ? `<span class="pp-estado on"><span class="pp-dot"></span>Activo</span>`
      : `<span class="pp-estado off"><span class="pp-dot"></span>Inactivo</span>`;

    const tipoHtml = p.nomeTipoPosto
      ? `<span class="pp-tipo-chip"><i class="ri-building-2-line"></i>${p.nomeTipoPosto}</span>`
      : '';

    const locHtml =
      p.nomeMunicipio || p.nomeProvincia
        ? `
      <div class="pp-meta">
        <i class="ri-map-pin-line"></i>
        <span>${p.nomeMunicipio ?? ''}${p.nomeMunicipio && p.nomeProvincia ? ', ' : ''}${p.nomeProvincia ?? ''}</span>
      </div>`
        : '';

    const clienteHtml = p.nomeCliente
      ? `
      <div class="pp-meta">
        <i class="ri-user-star-line"></i>
        <span>${p.nomeCliente}</span>
      </div>`
      : '';

    return `
      <div class="pp-card">
        <div class="pp-header">
          <div class="pp-chips">
            ${tipoHtml}
            ${estadoHtml}
          </div>
        </div>
        <div class="pp-body">
          <div class="pp-pin-icon risco-${risco}">
            <i class="ri-map-pin-2-line"></i>
          </div>
          <div class="pp-identity">
            <h3 class="pp-nome">${p.nome}</h3>
            ${locHtml}
            ${clienteHtml}
          </div>
        </div>
        <div class="pp-divider"></div>
        <div class="pp-kpis">
          <div class="pp-kpi">
            <i class="ri-team-line pp-kpi-icon blue"></i>
            <div>
              <span class="pp-kval">${p.numEfetivosTurno ?? 0}</span>
              <span class="pp-klbl">Ef./Turno</span>
            </div>
          </div>
          <div class="pp-kpi-sep"></div>
          <div class="pp-kpi">
            <i class="ri-time-line pp-kpi-icon purple"></i>
            <div>
              <span class="pp-kval">${p.numTurnosDia ?? 0}</span>
              <span class="pp-klbl">Turnos/Dia</span>
            </div>
          </div>
          <div class="pp-kpi-sep"></div>
          <div class="pp-kpi">
            <i class="${riscoIcone} pp-kpi-icon muted"></i>
            <div>
              <span class="pp-kval" style="color:${riscoColor}">${p.risco || '—'}</span>
              <span class="pp-klbl">Risco</span>
            </div>
          </div>
        </div>
        <div class="pp-footer">
          <code class="pp-codigo">${p.codigo || '—'}</code>
          <div class="pp-footer-btns">
            <button class="pp-btn-ver" onclick="window._postoVer(${p.id})">
              <i class="ri-eye-line"></i> Ver Detalhe
            </button>
            <button class="pp-btn-edit" onclick="window._postoEdit(${p.id})">
              <i class="ri-pencil-line"></i>
            </button>
          </div>
        </div>
      </div>`;
  }

  private getRiscoColor(risco?: string): string {
    const r = (risco || '').toLowerCase();
    if (r === 'critico' || r === 'crítico') return '#c0392b';
    if (r === 'alto') return '#e74c3c';
    if (r === 'moderado' || r === 'medio') return '#d97706';
    if (r === 'baixo') return '#27ae60';
    return '#9ca3af';
  }

  private setupPopupCallbacks(): void {
    window._postoVer = (id: number) => {
      this.ngZone.run(() => {
        const posto = this.postos.find((p) => p.id === id);
        if (posto) this.verPosto.emit(posto);
      });
    };
    window._postoEdit = (id: number) => {
      this.ngZone.run(() => {
        const posto = this.postos.find((p) => p.id === id);
        if (posto) this.editarPosto.emit(posto);
      });
    };
  }

  switchVista(v: 'mapa' | 'tabela'): void {
    this.vista = v;
    this.cdr.markForCheck();
    if (v === 'mapa') {
      setTimeout(() => {
        if (this.map) {
          this.map.invalidateSize();
          this.ngZone.runOutsideAngular(() => this.renderMarkers());
        } else {
          this.initMap();
        }
      }, 50);
    }
  }

  get postosComCoords(): number {
    return this.postos.filter(
      (p) =>
        p.latitude != null &&
        p.longitude != null &&
        p.latitude !== 0 &&
        p.longitude !== 0 &&
        !isNaN(Number(p.latitude)) &&
        !isNaN(Number(p.longitude)),
    ).length;
  }

  get postosAtivos(): number {
    return this.postos.filter((p) => p.ativo).length;
  }

  get postosCriticos(): number {
    return this.postos.filter((p) => {
      const r = (p.risco || '').toLowerCase();
      return r === 'critico' || r === 'crítico';
    }).length;
  }
}
